import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  gateReportOf,
  useDraftVersion,
  useSetVersionStatus,
  useUpdateDraftVersion,
  validationIssuesOf,
  type LifecycleVersion,
  type VersionOwner,
} from '@eis/core';
import { ConfirmAction, RequirePermission } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';

interface Issue {
  readonly path?: string;
  readonly code?: string;
  readonly message?: string;
}

/**
 * The DRAFT version editor (SDD-016 §4.4, D-V4), tarif AND group tarif.
 *
 * A draft may be incomplete — that is the point of it: the manifest is edited as a document, saved
 * as it is, and the service answers with the issues the publish gate WOULD refuse with, stored on
 * the draft and listed here with their JSON Pointers. Publishing runs the whole gate; the gate
 * numbers the version (D-V2), and "request major" is the one thing the author may add to that.
 *
 * The manifest is edited as JSON on purpose: a draft is by definition a document the typed form
 * cannot hold yet (a missing required section, a ref still unresolved), and the issues panel speaks
 * the same pointers the text does.
 */
export function DraftVersionEditor({
  owner,
  slug,
  generation,
  draftId,
  open,
  onClose,
  onPublished,
}: {
  readonly owner: VersionOwner;
  readonly slug: string;
  readonly generation: string;
  readonly draftId: string | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onPublished: (version: LifecycleVersion) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const draft = useDraftVersion({ owner, slug, generation, draftId: open ? draftId : undefined });
  const update = useUpdateDraftVersion();
  const setStatus = useSetVersionStatus();
  const [text, setText] = useState<string | undefined>(undefined);
  const [requestMajor, setRequestMajor] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const publishPermission = owner === 'tarif' ? p.products.publish : p.groupTariffs.publish;
  const updatePermission = owner === 'tarif' ? p.products.update : p.groupTariffs.update;

  const loaded = draft.data;
  const shown = text ?? JSON.stringify(loaded?.manifest ?? {}, null, 2);
  const parsed = parseObject(shown);
  const issues: readonly Issue[] = loaded?.validationIssues ?? [];
  const refusal = gateReportOf(setStatus.error);
  const refusedIssues: readonly Issue[] =
    refusal?.contractIssues ?? validationIssuesOf(setStatus.error);
  const busy = update.isPending || setStatus.isPending || draft.isFetching;

  const close = (): void => {
    setText(undefined);
    setRequestMajor(false);
    update.reset();
    setStatus.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      aria-labelledby="draft-editor-title"
    >
      <DialogTitle id="draft-editor-title">
        {t('authoring.draft.title')} {draftId}
      </DialogTitle>
      <DialogContent>
        {loaded === undefined ? (
          <Skeleton variant="rounded" height={240} />
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {loaded.basedOnErn !== undefined && loaded.basedOnErn !== '' && (
              <Typography variant="body2" color="text.secondary">
                {t('authoring.draft.basedOn', { version: loaded.basedOnErn })}
              </Typography>
            )}
            <TextField
              label={t('authoring.draft.manifest')}
              helperText={
                parsed === undefined
                  ? t('authoring.draft.invalidJson')
                  : t('authoring.draft.manifestHelp')
              }
              error={parsed === undefined}
              value={shown}
              onChange={(event) => {
                setText(event.target.value);
              }}
              multiline
              minRows={10}
              maxRows={22}
              fullWidth
              size="small"
              slotProps={{
                htmlInput: { spellCheck: false, 'aria-describedby': 'draft-issues' },
                input: { sx: { fontFamily: 'monospace' } },
              }}
            />

            <section id="draft-issues" aria-label={t('authoring.draft.issuesTitle')}>
              <Typography variant="subtitle2" component="h3">
                {t('authoring.draft.issuesTitle')}
              </Typography>
              {/*
                The status tones (prompt 24): open issues in `danger`, the complete draft in the
                `draft` tone — it is still a draft until it is published.
              */}
              {issues.length === 0 ? (
                <Alert
                  severity="success"
                  sx={{
                    bgcolor: 'var(--mui-palette-status-draft-surface)',
                    color: 'var(--mui-palette-status-draft-main)',
                    '& .MuiAlert-icon': { color: 'var(--mui-palette-status-draft-main)' },
                  }}
                >
                  {t('authoring.draft.noIssues')}
                </Alert>
              ) : (
                <IssueList
                  issues={issues}
                  rootLabel={t('authoring.draft.rootPath')}
                  tone="danger"
                />
              )}
            </section>

            {update.isSuccess && text === undefined && (
              <Alert severity="info">{t('authoring.draft.saved')}</Alert>
            )}
            {update.error !== null && <Alert severity="error">{update.error.message}</Alert>}
            {setStatus.isError && (
              <Alert severity="error">
                {t('authoring.draft.refused')}
                {refusedIssues.length > 0 && (
                  <IssueList issues={refusedIssues} rootLabel={t('authoring.draft.rootPath')} />
                )}
              </Alert>
            )}

            <RequirePermission anyOf={[publishPermission]}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={requestMajor}
                    onChange={(event) => {
                      setRequestMajor(event.target.checked);
                    }}
                  />
                }
                label={t('authoring.draft.requestMajor')}
              />
              <Typography variant="caption" color="text.secondary">
                {t('authoring.draft.requestMajorHelp')}
              </Typography>
            </RequirePermission>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <RequirePermission anyOf={[publishPermission]}>
          <Button
            color="warning"
            disabled={busy || loaded === undefined}
            onClick={() => {
              setDiscarding(true);
            }}
          >
            {t('authoring.draft.discard')}
          </Button>
        </RequirePermission>
        <Button onClick={close}>{t('authoring.draft.close')}</Button>
        <RequirePermission anyOf={[updatePermission]}>
          <Button
            variant="outlined"
            disabled={busy || parsed === undefined || text === undefined}
            onClick={() => {
              if (parsed === undefined || draftId === undefined) return;
              update.mutate(
                { owner, slug, generation, draftId, manifest: parsed },
                {
                  onSuccess: () => {
                    setText(undefined);
                  },
                },
              );
            }}
          >
            {t('authoring.draft.save')}
          </Button>
        </RequirePermission>
        <RequirePermission anyOf={[publishPermission]}>
          <Button
            variant="contained"
            // Publish = zero issues AND the gate (D-V4): an unsaved edit or an open issue first.
            disabled={busy || issues.length > 0 || text !== undefined || loaded === undefined}
            onClick={() => {
              if (draftId === undefined) return;
              setStatus.mutate(
                {
                  owner,
                  slug,
                  generation,
                  version: draftId,
                  status: 'VERSION_STATUS_PUBLISHED',
                  requestMajor,
                },
                {
                  onSuccess: (response) => {
                    const version = response.version;
                    if (version !== undefined) onPublished(version);
                    close();
                  },
                },
              );
            }}
          >
            {t('authoring.draft.publish')}
          </Button>
        </RequirePermission>
      </DialogActions>

      <ConfirmAction
        open={discarding}
        severity="destructive"
        title={t('authoring.draft.discardTitle')}
        description={t('authoring.draft.discardDescription')}
        confirmLabel={t('authoring.draft.discardConfirm')}
        busy={setStatus.isPending}
        onCancel={() => {
          setDiscarding(false);
        }}
        onConfirm={() => {
          setDiscarding(false);
          if (draftId === undefined) return;
          setStatus.mutate(
            { owner, slug, generation, version: draftId, status: 'VERSION_STATUS_DISCARDED' },
            { onSuccess: close },
          );
        }}
      />
    </Dialog>
  );
}

function IssueList({
  issues,
  rootLabel,
  tone,
}: {
  readonly issues: readonly Issue[];
  readonly rootLabel: string;
  /** Painted in a status tone (the open issues of the panel); plain inside an error alert. */
  readonly tone?: 'danger';
}): ReactNode {
  return (
    <List
      dense
      disablePadding
      sx={
        tone === undefined
          ? undefined
          : {
              px: 1.5,
              borderRadius: 1,
              bgcolor: `var(--mui-palette-status-${tone}-surface)`,
              '& .MuiListItemText-primary': { color: `var(--mui-palette-status-${tone}-main)` },
              '& .MuiListItemText-secondary': { color: 'var(--mui-palette-text-secondary)' },
            }
      }
    >
      {issues.map((issue, index) => (
        <ListItem key={`${issue.path ?? ''}-${issue.code ?? ''}-${String(index)}`} disableGutters>
          <ListItemText
            primary={issue.message}
            secondary={`${issue.path === undefined || issue.path === '' ? rootLabel : issue.path} · ${issue.code ?? ''}`}
          />
        </ListItem>
      ))}
    </List>
  );
}

function parseObject(text: string): Record<string, unknown> | undefined {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
